import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';

import { FlexLayoutModule } from '@ngbracket/ngx-layout';

import { ClientTranslationKeyModel } from '../models/language-locale.model';

import { TranslationManagerService } from '../services/translation-manager.service';

import { tap } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

import { ClientTranslationKeyPanelComponent } from '../client-translation-key-panel/client-translation-key-panel.component';
import { ClientTranslationAdminPaneComponent } from '../client-translation-admin-pane/client-translation-admin-pane.component';
import { TranslationKeyCreationService } from '../services/translation-key-creation/translation-key-creation.service';

@Component({
	selector: 'translation-manager',
	templateUrl: './translation-manager.component.html',
	styleUrls: ['./translation-manager.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		MatSidenavModule,
		MatToolbarModule,
		MatButtonModule,
		FlexLayoutModule,
		ClientTranslationKeyPanelComponent,
		ClientTranslationAdminPaneComponent
	]
})
export class TranslationManagerComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('clientTranslationKeyPanel', { static: false })
	clientTranslationKeyPanel: ClientTranslationKeyPanelComponent;
	@ViewChild('clientTranslationAdminPane', { static: false })
	clientTranslationAdminPane: ClientTranslationAdminPaneComponent;

	public readonly TOOL_TITLE: string = 'Translation Manager';
	public readonly ALLOW_TRANSLATION_AUTOMATION: boolean = true;
	public readonly ALLOW_NEW_KEY_CREATION: boolean = true;
	public $clientTranslationKeysObservable: Observable<ClientTranslationKeyModel[]>;

	private subscriptions: Subscription[] = [];

	constructor(
		private translationManagerService: TranslationManagerService,
		private translationKeyCreationService: TranslationKeyCreationService
	) {}

	async ngOnInit(): Promise<void> {
		this.$clientTranslationKeysObservable = this.configureClientTranslationKeyObservable();
		this.configureTranslationRefreshSub();
	}

	async ngAfterViewInit(): Promise<void> {
		// Wait for translation keys to load and set the first key as selected
		await this.$clientTranslationKeysObservable.toPromise();
		if (
			this.translationManagerService.clientTranslationKeys &&
			this.translationManagerService.clientTranslationKeys.length > 0 &&
			!this.clientTranslationKeyPanel.selectedTranslationKey
		) {
			this.clientTranslationKeyPanel.selectedTranslationKey =
				this.translationManagerService.clientTranslationKeys[0];
		}
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((sub) => {
			sub.unsubscribe();
		});
	}

	/**
	 * TODO - I can handle the toast messages/alerts here. Translation dataModifed could carry options in regard to this.
	 */
	private configureTranslationRefreshSub() {
		this.subscriptions.push(
			this.translationManagerService.translationDataModified.subscribe(async () => {
				await this.refreshTranslationStore(false);
			})
		);
	}

	/**
	 * Sets the correct value of the key panels selected key.
	 * If the current selected key still exists, we assign the selected key its value. Else we set it first element from server
	 * @note TODO - This find index is bad, I need to fix this
	 * @param translationKeys List of translationKeys from the server
	 */
	private setSelectedTranslationKey(translationKeys: ClientTranslationKeyModel[]): void {
		if (this.clientTranslationKeyPanel.selectedTranslationKey != null) {
			const selectedKeyIndex = translationKeys.findIndex((key) => {
				if (key.key == this.clientTranslationKeyPanel.selectedTranslationKey.key) {
					return key;
				}
			});

			if (selectedKeyIndex != -1) {
				this.clientTranslationKeyPanel.selectedTranslationKey = translationKeys[selectedKeyIndex];
			} else {
				this.clientTranslationKeyPanel.selectedTranslationKey = translationKeys[0];
				alert('Selected Key no longer exists');
			}
		} else {
			this.clientTranslationKeyPanel.selectedTranslationKey = translationKeys[0];
		}
	}

	/**
	 * @remarks Gets all translation keys from the server. Sets selected key in key panel
	 * @sets TranslationManagerService.clientTranslationKeys
	 * @returns ClientTranslationKeyModel
	 */
	private configureClientTranslationKeyObservable(): Observable<ClientTranslationKeyModel[]> {
		return this.translationManagerService.getClientTranslationKeys().pipe(
			tap((translationKeys: ClientTranslationKeyModel[]) => {
				this.setSelectedTranslationKey(translationKeys);
				this.translationManagerService.clientTranslationKeys = translationKeys;
			})
		);
	}

	/**
	 * @remarks See TranslationManagerService.createAndReTranslateAllIncompleteKeys for details. Alerts user of result
	 * @note Please change documentation remarks if this function grows
	 */
	public async translateAllKeysByLanguage() {
		const translatedKeys = await this.translationManagerService.createAndReTranslateAllIncompleteKeys();
		alert(JSON.stringify(translatedKeys, null, 2));
	}

	/**
	 * Retrieves all translation records from the server.
	 * @param showCompletedMessage If you wish to alert the user that a refresh event was completed, such as a manual button click
	 */
	public async refreshTranslationStore(showCompletedMessage: boolean = true) {
		await this.$clientTranslationKeysObservable.toPromise();

		if (showCompletedMessage) {
			alert('Refresh complete');
		}
	}

	public openNewKeyCreationModal() {
		this.translationKeyCreationService.openKeyCreationModal();
	}
}
